import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AppService } from './app.service';
import { DatabaseService } from './database.service';
import { HelperService } from '.';
import { AssignedPackage, AssignedPackageSession } from '../models';
import { equalTo, orderByChild } from '@angular/fire/database';

export interface ICountryObject {
  iso2: string;
  name: string;
}

export interface IStateObject {
  code: number | string;
  country: string;
  name: string;
}

export interface IPaymentMethod {
  id: string;
  customer: string;
}

export interface ITokenRequest {
  id: string, 
  country: string, 
  state: string, 
  address: string, 
  city: string, 
  zip: string
}

@Injectable({
  providedIn: 'root'
})
export class BillingService {

  constructor(
    private appService: AppService,
    private dbService: DatabaseService,
    private helperService: HelperService,
    private http: HttpClient,
  ) { }

  public getSystemPayments(limit = 20, startAfter?: string): Promise<any> {
    let url = `${this.appService.getApiURL()}/billing/system/payments?limit=${limit}`;
    if(startAfter) {
      url += `&startAfter=${startAfter}`
    }
    return new Promise((resolve, reject) => {
      this.http.get(url)
      .toPromise()
      .then(res => { resolve(<string>res) })
      .catch(err => { reject(err) })
    });
  }

  public async loadCountries(): Promise<ICountryObject[]> {
    const data = await this.dbService.getSnapshot('/globalSettings/billing/countries');
    if(data) {
      const toReturn: ICountryObject[] = [];
      for (const countryId in data) {
        if (data.hasOwnProperty(countryId)) {
          const country = data[countryId];
          toReturn.push(country);
        }
      }
      return toReturn
    } else {
      return undefined
    }
  }

  public async loadStates(): Promise<IStateObject[]> {
    const data = await this.dbService.getSnapshot('/globalSettings/billing/states');
    if(data) {
      const toReturn: IStateObject[] = [];
      for (const stateId in data) {
        if (data.hasOwnProperty(stateId)) {
          const state = data[stateId];
          toReturn.push(state);
        }
      }
      return toReturn
    } else {
      return undefined
    }
  }

  public loadPlans() {
    return this.dbService.getSnapshot(`globalSettings/plans`);
  }

  public getStatesByCountry(countryCode: string, states: IStateObject[]) {
    return states? states.filter(state => {return state.country === countryCode}) : null
  }

  public async getAssignedPackage(packageId: string) {
    const snap = await this.dbService.getSnapshot(`assignedPackages/${packageId}`);
    return snap? new AssignedPackage({id: packageId, ...snap}) : undefined;
  }

  public getAssignedPackageProperty(packageId: string, property: string) {
    return this.dbService.getSnapshot(`assignedPackages/${packageId}/${property}`);
  }

  public assignPackageToUser(pkg: Partial<AssignedPackage>) {
    return this.dbService.pushByRef('assignedPackages', pkg)
  }

  public updateAssignedPackage(assignmentId: string, data: Partial<AssignedPackage>) {
    return this.dbService.updateRef(`assignedPackages/${assignmentId}`, data);
  }

  public deleteAssignedPackage(assignmentId: string) {
    return this.dbService.removeRef(`assignedPackages/${assignmentId}`);
  }

  public cancelAssignedPackage(assignmentId: string) {
    return new Promise((resolve, reject) => {
      this.http.post(`${this.appService.getApiURL()}/billing/package/${assignmentId}/cancel`, {status: "canceled"})
      .toPromise()
      .then(res => { resolve(<AssignedPackage>res) })
      .catch(err => { reject(err) })
    });
  }

  public upgradeAssignedPackage(assignmentId: string, priceId: string) {
    return new Promise((resolve, reject) => {
      this.http.post(`${this.appService.getApiURL()}/billing/package/${assignmentId}/upgrade`, {priceId: priceId})
      .toPromise()
      .then(res => { resolve(<AssignedPackage>res) })
      .catch(err => { reject(err) })
    });
  }

  public changeAssignedPackageRenwal(assignmentId: string, date: string) {
    return new Promise((resolve, reject) => {
      this.http.post(`${this.appService.getApiURL()}/billing/package/${assignmentId}/renewal`, {date: date})
      .toPromise()
      .then(res => { resolve(<AssignedPackage>res) })
      .catch(err => { reject(err) })
    });
  }

  public refundAssignedPackage(assignmentId: string, invoiceId?: string, amount?: number) {
    return new Promise((resolve, reject) => {
      this.http.post(`${this.appService.getApiURL()}/billing/package/${assignmentId}/refund`, {status: "canceled", invoiceId: invoiceId, amount: amount})
      .toPromise()
      .then(res => { resolve(<AssignedPackage>res) })
      .catch(err => { reject(err) })
    });
  }

  public unpauseAssignedPackage(assignmentId: string) {
    return new Promise((resolve, reject) => {
      this.http.post(`${this.appService.getApiURL()}/billing/package/${assignmentId}/unpause`, {status: "canceled"})
      .toPromise()
      .then(res => { resolve(<AssignedPackage>res) })
      .catch(err => { reject(err) })
    });
  }

  public deferAssignedPackageInvoice(assignmentId: string, invoiceId?: string, amount?: number) {
    return new Promise((resolve, reject) => {
      this.http.post(`${this.appService.getApiURL()}/billing/package/${assignmentId}/defer`, {invoiceId: invoiceId, amount: amount})
      .toPromise()
      .then(res => { resolve(<AssignedPackage>res) })
      .catch(err => { reject(err) })
    });
  }

  public pauseAssignedPackage(assignmentId: string) {
    return new Promise((resolve, reject) => {
      this.http.post(`${this.appService.getApiURL()}/billing/package/${assignmentId}/pause`, {status: "canceled"})
      .toPromise()
      .then(res => { resolve(<AssignedPackage>res) })
      .catch(err => { reject(err) })
    });
  }

  public async getAssignedPackagesByOrganization(orgId: string): Promise<AssignedPackage[]> {
    const val = await this.dbService.getSnapshot(`assignedPackages`, orderByChild("organization"), equalTo(orgId));
    return val? this.helperService.transformFirebaseObjectToArray(val).map(pkg => {return new AssignedPackage(pkg)}) : null;
  }

  public getAssignedPackagesListByUser(userId: string) {
    return this.dbService.getListByRefTest(`assignedPackages`, orderByChild("user"), equalTo(userId));
  }

  public async getAssignedPackagesByUser(userId: string): Promise<AssignedPackage[]> {
    const val = await this.dbService.getSnapshot(`assignedPackages`, orderByChild("user"), equalTo(userId));
    return val? this.helperService.transformFirebaseObjectToArray(val).map(pkg => {return new AssignedPackage(pkg)}) : null;
  }

  public async getAssignedPackagesByUserOrganization(userId: string, orgId: string): Promise<any[]> {
    const packages = await this.getAssignedPackagesByUser(userId);
    if(packages) {
      return packages.filter(pkg => {return pkg.organization === orgId});
    } else {
      return undefined
    }
  }

  addAssignedPackageSession(packageId: string, data: Partial<AssignedPackageSession>) {
    return this.dbService.pushByRef(`assignedPackages/${packageId}/sessionsUsed`, data)
  }

  addScheduledPackageSession(packageId: string, appointmentId: string, timestamp: string, trainer: {trainerId: string, trainerName: string}, source: "appointment" = "appointment") {
    return this.dbService.setRef(`assignedPackages/${packageId}/sessionsScheduled/${appointmentId}`, {
      createdOn: new Date().toISOString(),
      source: source,
      timestamp: timestamp,
      ...trainer
    });
  }

  getUserPaymentMethods(userId: string): Promise<IPaymentMethod[]> {
    return new Promise((resolve, reject) => {
      this.http.get(`${this.appService.getApiURL()}/billing/${userId}/methods`)
      .toPromise()
      .then(res => { resolve(<IPaymentMethod[]>res) })
      .catch(err => { reject(err) })
    });
  }

  confirmAssignedPackage(packageId: string, data): Promise<any> {
    return this.http.post(`${this.appService.getApiURL()}/billing/package/${packageId}/confirm`, data)
    .toPromise()
    .then(this.helperService.handlePromiseSuccess)
    .catch(this.helperService.handlePromiseRejection)
  }

  upgradePlan(userId: string, subscription: any) {
    return new Promise((resolve, reject) => {
      this.http.post(`${this.appService.getApiURL()}/billing/${userId}/upgrade`, subscription)
      .toPromise()
      .then(res => { resolve(res) })
      .catch(err => { reject(err) })
    });
  }

  getUserDefaultPaymentMethod(userId: string): Promise<IPaymentMethod[]> {
    return new Promise((resolve, reject) => {
      this.http.get(`${this.appService.getApiURL()}/billing/${userId}/default`)
      .toPromise()
      .then(res => { resolve(<IPaymentMethod[]>res) })
      .catch(err => { reject(err) })
    });
  }

  addUserPaymentMethod(userId, data: ITokenRequest): Promise<any> {
    return this.http.post(`${this.appService.getApiURL()}/billing/${userId}/methods`, data)
    .toPromise()
    .then(this.helperService.handlePromiseSuccess)
    .catch(this.helperService.handlePromiseRejection)
  }

  removeUserPaymentMethod(userId: string, methodId: string) {
    return this.http.delete(`${this.appService.getApiURL()}/billing/${userId}/methods/${methodId}`)
    .toPromise()
    .then(this.helperService.handlePromiseSuccess)
    .catch(this.helperService.handlePromiseRejection)
  }

  getUserPackageDetail(packageId): Promise<AssignedPackage> {
    return new Promise((resolve, reject) => {
      this.http.get(`${this.appService.getApiURL()}/billing/package/${packageId}/detail`)
      .toPromise()
      .then(res => { resolve(<AssignedPackage>res) })
      .catch(err => { reject(err) })
    });
  }

  getUserPackageInvoices(packageId, lastItem?: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const url = `${this.appService.getApiURL()}/billing/package/${packageId}/invoicesV2` + (lastItem? '?last=' + lastItem : "")
      this.http.get(url)
      .toPromise()
      .then(res => { resolve(<any>res) })
      .catch(err => { reject(err) })
    });
  }

  getUserPackagePayments(packageId): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http.get(`${this.appService.getApiURL()}/billing/package/${packageId}/payments`)
      .toPromise()
      .then(res => { resolve(<any>res) })
      .catch(err => { reject(err) })
    });
  }

  purchaseUserPackage(packageId, data: any): Promise<AssignedPackage> {
    return this.http.post(`${this.appService.getApiURL()}/billing/package/${packageId}/purchase`, data)
    .toPromise()
    .then(this.helperService.handlePromiseSuccess)
    .catch(this.helperService.handlePromiseRejection)
  }

  payUserPackage(packageId, data: any): Promise<AssignedPackage> {
    return this.http.post(`${this.appService.getApiURL()}/billing/package/${packageId}/pay`, data)
    .toPromise()
    .then(this.helperService.handlePromiseSuccess)
    .catch(this.helperService.handlePromiseRejection)
  }

  payUserPackageInvoice(packageId: string, invoiceId: string) {
    return this.http.put(`${this.appService.getApiURL()}/billing/package/${packageId}/invoices/${invoiceId}`, {})
    .toPromise()
    .then(this.helperService.handlePromiseSuccess)
    .catch(this.helperService.handlePromiseRejection)
  }

  getUserPayments(userId: string, organizationId: string) {
    return new Promise((resolve, reject) => {
      this.http.get(`${this.appService.getApiURL()}/billing/${userId}/payments?organization=${organizationId}`)
      .toPromise()
      .then(res => { resolve(<AssignedPackage>res) })
      .catch(err => { reject(err) })
    });
  }

  payUserPayment(paymentId, data: any): Promise<AssignedPackage> {
    return new Promise((resolve, reject) => {
      this.http.post(`${this.appService.getApiURL()}/billing/payment/${paymentId}/pay`, data)
      .toPromise()
      .then(res => { resolve(<AssignedPackage>res) })
      .catch(err => { reject(err) })
    });
  }

  getCancelationReasons() {
    return [
      {
        text: "It's too expensive right now",
        value: "price"
      },
      {
        text: "I'm not using it enough",
        value: "usage"
      },
      {
        text: "It's missing a feature I need",
        value: "feature"
      },
      {
        text: "Business is slow, not enough clients",
        value: "slow"
      },
      {
        text: "I'm switching to another platform",
        value: "competitor"
      },
      {
        text: "Something else",
        value: "other"
      }
    ]
  }

  /** Legacy admin/reporting labels mapped onto the retention reason taxonomy. */
  getLegacyCancelationReasonLabel(value: string): string {
    const map = {
      noClients: "I am no longer training clients",
      trianingNeeds: "This app doesn't meet my specific personal training needs",
      anotherApp: "I am using another application",
      priceExpensive: "The price of this application is too expensive",
      other: "Other",
      price: "It's too expensive right now",
      usage: "I'm not using it enough",
      feature: "It's missing a feature I need",
      slow: "Business is slow, not enough clients",
      competitor: "I'm switching to another platform",
    };
    return map[value] || value;
  }
}
